import { useEffect, useState } from 'react';
import type { IconNameField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';

/**
 * Material Symbols name picker — the Shopify model. The merchant types a Google Material Symbols name
 * (e.g. `check_circle`, `local_shipping`) and the storefront renders that glyph via the webfont; a
 * "View all available icons" link opens the official gallery to copy a name. Stored value is the plain
 * name STRING (a SettingValue), so nothing in the persistence layer changes.
 *
 * Commit on BLUR / Enter only (NOT per keystroke): the Material Symbols webfont renders a half-typed name
 * as ugly literal text in the live preview (e.g. "FAVORIT" before "favorite" resolves), so we keep the
 * preview stable while the merchant is typing and only apply the value once the name is finished.
 */
export function IconNameControl({
  field,
  value,
  onChange,
}: {
  field: IconNameField;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);

  const commit = () => {
    const next = local.trim();
    if (next !== (value ?? '')) onChange(next);
  };

  return (
    <FieldRow id={field.id} label={field.label} info={field.info}>
      <input
        id={field.id}
        className="bld-input"
        type="text"
        value={local}
        placeholder={field.placeholder ?? 'check_circle'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur(); // triggers onBlur → commit
          }
        }}
      />
      <a
        className="bld-iconname__link"
        href="https://fonts.google.com/icons"
        target="_blank"
        rel="noopener noreferrer"
      >
        View all available icons
      </a>
    </FieldRow>
  );
}
